import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { ClashTestDetails, ClashSettings } from "@ServerTypes/clashTests/clashTests.schema";

export interface ClashDetailsState extends ClashTestDetails {
  isFetch: boolean;
  settings?: ClashSettings;
  // Folder-permission users array on the clash doc (consumed by ModuleUsersAccess).
  users?: unknown[];
}

const clashDetailsSlice = createSlice({
  name: "clashDetails",
  initialState: { isFetch: false } as ClashDetailsState,
  reducers: {
    setClashDetails: (_state: ClashDetailsState, action: PayloadAction<ClashTestDetails>) => ({
      ...action.payload,
      isFetch: true,
    }),
    setClashSettings: (_state: ClashDetailsState, action: PayloadAction<Partial<ClashSettings>>) => ({
      ..._state,
      settings: { ..._state.settings, ...action.payload },
    }),
    resetClashDetails: (_state: ClashDetailsState, _action: PayloadAction<void>) => ({ isFetch: false }),
  },
});

const { setClashSettings: _setClashSettingsRaw } = clashDetailsSlice.actions;

// `setClashDetails` is intentionally exported raw (NOT wrapped with `Exact<>`).
// The consumer in actions/clash.ts uses `_.get(response, "data.result", {})`
// which yields `ClashTestDetails | {}` — the `{}` branch fails Exact.
// `resetClashDetails` has a `void` payload so it's exported raw too.
export const { setClashDetails, resetClashDetails } = clashDetailsSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setClashSettings = <T extends Exact<Partial<ClashSettings>, T>>(payload: T) =>
  _setClashSettingsRaw(payload);

export const clashDetails = clashDetailsSlice.reducer;

export default {
  clashDetails,
};
